<template>
  <div class="brick-documentation-tab">
    <div class="text-h6 q-mb-md">BRICK Schema Integration</div>

    <!-- Basic BRICK Info -->
    <q-card flat bordered>
      <q-card-section>
        <div class="text-subtitle2 q-mb-sm">BRICK Class</div>
        <q-input
          :model-value="modelValue.brick_uri"
          label="BRICK URI"
          outlined
          dense
          readonly
          class="q-mb-md"
        >
          <template #prepend>
            <q-icon name="link" />
          </template>
        </q-input>
      </q-card-section>
    </q-card>

    <!-- Database Relationships -->
    <q-card flat bordered class="q-mt-md">
      <q-card-section>
        <div class="text-subtitle2 q-mb-sm">Database Relationships</div>
        <div v-if="equipmentDetails">
          <q-list dense>
            <!-- Zone Sections (hasPart) -->
            <q-item v-if="equipmentDetails.zoneSections && equipmentDetails.zoneSections.length > 0">
              <q-item-section avatar>
                <q-icon name="account_tree" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Zone Sections (hasPart)</q-item-label>
                <q-item-label caption>
                  {{ equipmentDetails.zoneSections.map(z => z.name).join(', ') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <!-- Parent Equipment (isPartOf) -->
            <q-item v-if="equipmentDetails.parent_equipment_id">
              <q-item-section avatar>
                <q-icon name="input" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Parent Equipment (isPartOf)</q-item-label>
                <q-item-label caption>
                  {{ equipmentDetails.parent_equipment?.name || `Equipment #${equipmentDetails.parent_equipment_id}` }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <!-- Feeds Equipment -->
            <q-item v-if="equipmentDetails.feeds_equipment && equipmentDetails.feeds_equipment.length > 0">
              <q-item-section avatar>
                <q-icon name="arrow_forward" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Feeds</q-item-label>
                <q-item-label caption>
                  {{ equipmentDetails.feeds_equipment.map(e => e.name || `Equipment #${e.id}`).join(', ') }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <!-- Devices (hasPoint) -->
            <q-item v-if="equipmentDetails.devices && equipmentDetails.devices.length > 0">
              <q-item-section avatar>
                <q-icon name="sensors" />
              </q-item-section>
              <q-item-section>
                <q-item-label>Devices (hasPoint)</q-item-label>
                <q-item-label caption>
                  {{ equipmentDetails.devices.length }} device(s)
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <div v-else class="text-grey-5">
          No database relationships found
        </div>
      </q-card-section>
    </q-card>

    <!-- Custom BRICK Relationships -->
    <q-card flat bordered class="q-mt-md">
      <q-card-section>
        <div class="text-subtitle2 q-mb-sm">Custom BRICK Relationships</div>

        <div v-if="modelValue.brick_relationships && modelValue.brick_relationships.length">
          <q-list bordered separator class="q-mb-md">
            <q-item v-for="(rel, index) in modelValue.brick_relationships" :key="index">
              <q-item-section avatar>
                <q-icon :name="getRelationshipIcon(rel.predicate)" />
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ rel.predicate }}</q-item-label>
                <q-item-label caption>{{ rel.object }}</q-item-label>
              </q-item-section>
              <q-item-section side>
                <q-btn
                  flat
                  round
                  dense
                  icon="delete"
                  color="negative"
                  @click="emit('remove-relationship', index)"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <div v-else class="text-grey-5 q-mb-md">
          No custom relationships defined
        </div>

        <q-btn
          color="primary"
          icon="add"
          label="Add Relationship"
          outline
          @click="emit('add-relationship')"
        />
      </q-card-section>
    </q-card>

    <!-- TTL Preview -->
    <q-card flat bordered class="q-mt-md">
      <q-card-section>
        <div class="row items-center q-mb-sm">
          <div class="text-subtitle2">TTL Preview</div>
          <q-space />
          <q-btn
            flat
            round
            icon="refresh"
            size="sm"
            @click="emit('refresh-ttl')"
          >
            <q-tooltip>Refresh TTL</q-tooltip>
          </q-btn>
        </div>
        <pre class="brick-ttl-preview">{{ ttlPreview || 'TTL generation requires useBrickSchemaVisualization composable' }}</pre>
      </q-card-section>
    </q-card>
  </div>
</template>

<script setup>
/**
 * BRICK Documentation Tab Component
 *
 * Displays BRICK schema integration, relationships, and TTL preview
 *
 * - TTL generation
 * - Relationship rendering
 * - Schema validation
 */

  const props = defineProps({
    modelValue: {
      type: Object,
      required: true,
      default: () => ({
        brick_uri: '',
        brick_relationships: [],
      }),
    },
    equipmentDetails: {
      type: Object,
      default: null,
    },
    ttlPreview: {
      type: String,
      default: '',
    },
  });

  const emit = defineEmits([
    'add-relationship',
    'remove-relationship',
    'refresh-ttl',
  ]);

  function getRelationshipIcon(predicate) {
    const iconMap = {
      'brick:feeds': 'arrow_forward',
      'brick:isFedBy': 'arrow_back',
      'brick:hasPoint': 'sensors',
      'brick:isPartOf': 'account_tree',
      'brick:hasLocation': 'location_on',
      'brick:controls': 'settings_remote',
      'brick:isControlledBy': 'settings',
      'brick:meters': 'speed',
      'brick:isMeteredBy': 'analytics',
    };
    return iconMap[predicate] || 'link';
  }
</script>

<style scoped>
.brick-documentation-tab {
  height: 100%;
}

.brick-ttl-preview {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
